import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  Calendar,
  Fuel,
  Gauge,
  Settings2,
  MapPin,
  Palette,
  Users,
  Heart,
  ArrowLeft,
  CheckCircle2,
  Flag,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { api, INR, KM, waLink, formatApiError } from "../lib/gj";
import { useAuth } from "../lib/auth";

const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 p-4 rounded-lg bg-[#F3F0EA]/60 border border-[#0A192F]/5">
    <Icon className="w-4 h-4 text-[#F97316]" />
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#718096] font-semibold">{label}</div>
      <div className="text-sm font-bold text-[#0A192F]">{value}</div>
    </div>
  </div>
);

export default function CarDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [car, setCar] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [favourited, setFavourited] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const submitReport = async () => {
    if (reportReason.trim().length < 5) {
      toast.error("Please share a bit more detail (min 5 characters).");
      return;
    }
    setReportSubmitting(true);
    try {
      await api.post("/report-listing", {
        car_id: id,
        reason: reportReason.trim(),
        reporter_name: user?.name || null,
        reporter_email: user?.email || null,
        reporter_phone: user?.phone || null,
      });
      toast.success("Report sent to the Gaddi Junction team. Thank you!");
      setReportOpen(false);
      setReportReason("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send report");
    } finally {
      setReportSubmitting(false);
    }
  };

  const fetchCar = useCallback(async () => {
    try {
      const { data } = await api.get(`/cars/${id}`);
      setCar(data);
    } catch (err) {
      console.warn("[car-detail] not found:", err?.response?.status);
      setCar(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCar();
  }, [fetchCar]);

  const onEnquire = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please share your name and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries", { car_id: id, ...form });
      toast.success("Enquiry sent! The dealer will contact you shortly.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  const onFavourite = async () => {
    if (!user || !user.id) {
      toast.info("Login to save favourites.");
      return;
    }
    try {
      if (!favourited) {
        await api.post(`/favourites/${id}`);
        setFavourited(true);
        toast.success("Saved to favourites");
      } else {
        await api.delete(`/favourites/${id}`);
        setFavourited(false);
      }
    } catch {
      toast.error("Could not update favourites");
    }
  };

  if (car === null)
    return <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 text-center">Loading car…</div>;
  if (car === false)
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-20 text-center">
        <div className="font-heading text-2xl font-bold text-[#0A192F]">Car not found</div>
        <Link to="/cars" className="text-[#F97316] mt-4 inline-block">Browse all cars</Link>
      </div>
    );

  const images = car.images?.length
    ? car.images
    : ["https://images.unsplash.com/photo-1542362567-b07e54358753?w=1200"];

  const waMessage = `Hi, I am interested in your ${car.brand} ${car.model} (${car.year}) listed on Gaddi Junction for ${INR(car.price)}. Is it still available?`;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="car-detail-page">
      <Link to="/cars" className="text-sm text-[#4A5568] hover:text-[#0A192F] inline-flex items-center gap-1 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to all cars
      </Link>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
        <div>
          <div className="relative aspect-[16/10] rounded-xl overflow-hidden bg-[#F3F0EA] gj-shadow-card">
            <img
              src={images[imgIdx]}
              alt={`${car.brand} ${car.model}`}
              className="w-full h-full object-cover"
            />
            <button
              onClick={onFavourite}
              data-testid="fav-toggle-btn"
              className={`absolute top-4 right-4 w-10 h-10 grid place-items-center rounded-full backdrop-blur-md transition-colors ${
                favourited ? "bg-[#F97316] text-[#0A192F]" : "bg-white/85 text-[#0A192F] hover:bg-white"
              }`}
            >
              <Heart className={`w-5 h-5 ${favourited ? "fill-current" : ""}`} />
            </button>
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {images.map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  onClick={() => setImgIdx(i)}
                  className={`relative w-20 h-16 rounded-md overflow-hidden border-2 shrink-0 ${
                    imgIdx === i ? "border-[#F97316]" : "border-transparent"
                  }`}
                >
                  <img src={src} alt="thumb" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}

          <div className="mt-8">
            <div className="text-xs uppercase tracking-[0.18em] text-[#F97316] font-bold">
              {car.brand}
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#0A192F] mt-1">
              {car.model}
            </h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-[#4A5568]">
              <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {car.location}</span>
              <span>·</span>
              <span>{car.year}</span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
              <Spec icon={Calendar} label="Year" value={car.year} />
              <Spec icon={Gauge} label="KM Driven" value={KM(car.km_driven)} />
              <Spec icon={Fuel} label="Fuel" value={car.fuel_type} />
              <Spec icon={Settings2} label="Transmission" value={car.transmission} />
              <Spec icon={Users} label="Owners" value={car.owners || 1} />
              {car.color && <Spec icon={Palette} label="Color" value={car.color} />}
            </div>

            {car.description && (
              <div className="mt-10">
                <h2 className="font-heading text-xl font-bold text-[#0A192F]">About this car</h2>
                <p className="text-[#4A5568] mt-3 leading-relaxed">{car.description}</p>
              </div>
            )}

            <div className="mt-10 bg-[#0A192F] text-white rounded-xl p-6 grid sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#F97316] mt-0.5" />
                <div>
                  <div className="font-semibold">Inspection Promise</div>
                  <div className="text-xs text-white/70 mt-1">Verified by dealer, ready-to-drive</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-[#F97316] mt-0.5" />
                <div>
                  <div className="font-semibold">Document Support</div>
                  <div className="text-xs text-white/70 mt-1">RC transfer & insurance help</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column: price + enquiry */}
        <aside className="space-y-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl p-6 gj-shadow-card sticky top-24">
            <div className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">Listed Price</div>
            <div className="font-heading text-3xl font-extrabold text-[#0A192F] mt-1">{INR(car.price)}</div>

            <div className="mt-6 border-t border-[#0A192F]/5 pt-5">
              <div className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">Seller</div>
              <div className="font-bold text-[#0A192F] mt-1">{car.dealer?.name || car.dealer_name || "Verified Dealer"}</div>
              {car.dealer?.city && <div className="text-sm text-[#4A5568]">{car.dealer.city}</div>}
            </div>

            <div className="mt-6 grid grid-cols-1 gap-2">
              <a
                href={waLink(car.phone || car.dealer?.phone, waMessage)}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] hover:bg-[#1EBE55] text-white rounded-md px-5 py-3 font-semibold flex items-center justify-center gap-2 transition-colors"
                data-testid="whatsapp-enquiry-btn"
              >
                <MessageCircle className="w-4 h-4" /> Contact Seller on WhatsApp
              </a>
              <a
                href={`tel:+91${(car.phone || car.dealer?.phone || "8910884194").toString().replace(/\D/g, "").slice(-10)}`}
                className="border border-[#0A192F] text-[#0A192F] rounded-md px-5 py-3 font-medium hover:bg-[#0A192F]/5 transition-colors flex items-center justify-center gap-2"
                data-testid="call-dealer-btn"
              >
                <Phone className="w-4 h-4" /> Call seller
              </a>
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                data-testid="report-listing-btn"
                className="mt-1 text-xs text-[#718096] hover:text-red-600 inline-flex items-center justify-center gap-1 py-1 transition-colors"
              >
                <Flag className="w-3 h-3" /> Report Listing
              </button>
            </div>
          </div>

          <form
            onSubmit={onEnquire}
            data-testid="enquiry-form"
            className="bg-white border border-[#0A192F]/10 rounded-xl p-6"
          >
            <div className="font-heading text-lg font-bold text-[#0A192F]">Send an enquiry</div>
            <p className="text-xs text-[#718096] mt-1">Dealer typically replies within a few hours.</p>
            <div className="space-y-3 mt-4">
              <div>
                <Label htmlFor="enq-name">Your Name</Label>
                <Input
                  id="enq-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Arjun Das"
                  data-testid="enquiry-name"
                />
              </div>
              <div>
                <Label htmlFor="enq-phone">Phone</Label>
                <Input
                  id="enq-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="98XXXXXXXX"
                  data-testid="enquiry-phone"
                />
              </div>
              <div>
                <Label htmlFor="enq-email">Email (optional)</Label>
                <Input
                  id="enq-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@email.com"
                  data-testid="enquiry-email"
                />
              </div>
              <div>
                <Label htmlFor="enq-msg">Message</Label>
                <Textarea
                  id="enq-msg"
                  rows={3}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="When can I test drive?"
                  data-testid="enquiry-message"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#0A192F] hover:bg-[#112240] text-white"
                data-testid="enquiry-submit-btn"
              >
                {submitting ? "Sending…" : "Send Enquiry"}
              </Button>
            </div>
          </form>
        </aside>
      </div>

      {/* Report Listing modal */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-listing-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Flag className="w-5 h-5" /> Report this listing
            </DialogTitle>
            <DialogDescription className="text-[#4A5568] leading-relaxed">
              Help us keep Gaddi Junction safe. Tell us what's wrong with this
              listing — fraud, wrong pricing, misleading photos, duplicate, or
              anything else. Our team at{" "}
              <span className="font-semibold text-[#0A192F]">
                support@gaddijunction.in
              </span>{" "}
              will review it.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="report-reason">Reason</Label>
              <Textarea
                id="report-reason"
                rows={4}
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="e.g. The seller is asking for an advance payment / photos don't match…"
                maxLength={1000}
                data-testid="report-reason-input"
              />
              <div className="text-[11px] text-[#718096] mt-1 text-right">
                {reportReason.length}/1000
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setReportOpen(false)}
              data-testid="report-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submitReport}
              disabled={reportSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="report-submit-btn"
            >
              {reportSubmitting ? "Sending…" : "Submit Report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
